import { DataGrid } from '@mui/x-data-grid';
// src/pages/admin/PromotionManagementPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Typography, Paper, Alert, TextField, Select, MenuItem,
    InputLabel, FormControl, Switch, FormControlLabel, Autocomplete, Grid, CircularProgress
} from '@mui/material';
import { esES } from '@mui/x-data-grid/locales';
import api from '../../api/index.js';

const PromotionManagementPage = () => {
    // --- ESTADOS ---
    const [promotions, setPromotions] = useState([]);
    const [products, setProducts] = useState([]); // Para el Autocomplete
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newPromo, setNewPromo] = useState({
        name: '', description: '', points_to_award: 1, is_active: true,
        condition_type: 'DAY_OF_WEEK', condition_value: '1', product_ids: []
    });

    // --- CARGA DE DATOS ---
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            // Hacemos las peticiones en paralelo
            const [promosRes, productsRes] = await Promise.all([
                api.get('/promotions'),
                api.get('/products') // Necesario para el formulario
            ]);
            setPromotions(promosRes.data);
            setProducts(productsRes.data);
        } catch (err) {
            console.error("Error al cargar datos de promociones:", err);
            setError('No se pudieron cargar los datos necesarios.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- MANEJADORES ---
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewPromo(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleProductsChange = (event, value) => {
        // 'value' es el array de objetos de producto seleccionados
        setNewPromo(prev => ({ ...prev, product_ids: value.map(p => p.id) }));
    };

    const handleCreate = useCallback(async () => {
        setError('');
        if (!newPromo.name || !newPromo.product_ids || newPromo.product_ids.length === 0) {
            setError("Nombre y al menos un producto son requeridos.");
            return;
        }

        const payload = {
            ...newPromo,
            conditions: [{ type: newPromo.condition_type, value: newPromo.condition_value }]
        };
        
        try {
            await api.post('/promotions', payload);
            alert('Promoción creada exitosamente.');
            await fetchData(); // Recargar todos los datos
        } catch (err) {
            setError(err.response?.data?.message || 'Error al crear la promoción.');
        }
    }, [newPromo, fetchData]);

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar esta promoción?')) {
            try {
                await api.delete(`/promotions/${id}`);
                alert('Promoción eliminada.');
                await fetchData(); // Recargar todos los datos
            } catch (err) {
                setError(err.response?.data?.message || 'Error al eliminar la promoción.');
            }
        }
    };

    // --- COLUMNAS Y DATOS PARA SELECTS ---
    const columns = [
        { field: 'name', headerName: 'Nombre', width: 300 },
        { field: 'points_to_award', headerName: 'Puntos', width: 100 },
        { field: 'is_active', headerName: 'Activa', type: 'boolean', width: 100 },
        // Las columnas 'conditions' y 'product_count' se eliminan porque el backend ya no las envía
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <Button variant="outlined" color="error" size="small" onClick={() => handleDelete(params.row.id)}>
                    Eliminar
                </Button>
            )
        }
    ];

    const daysOfWeek = [
        { label: 'Lunes', value: '1' }, { label: 'Martes', value: '2' },
        { label: 'Miércoles', value: '3' }, { label: 'Jueves', value: '4' },
        { label: 'Viernes', value: '5' }, { label: 'Sábado', value: '6' },
        { label: 'Domingo', value: '0' }
    ];

    // --- RENDERIZADO ---
    if (loading) {
        return <CircularProgress />;
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Gestión de Promociones</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6">Crear / Editar Promoción</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Nombre" name="name" value={newPromo.name} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Puntos a Otorgar" name="points_to_award" type="number" value={newPromo.points_to_award} onChange={handleInputChange} /></Grid>
                    <Grid item xs={12}><Autocomplete
                        multiple
                        filterSelectedOptions
                        options={products}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={handleProductsChange}
                        renderInput={(params) => <TextField {...params} label="Productos de la Promoción" />}
                    /></Grid>
                    <Grid item xs={12} md={6}><FormControl fullWidth>
                        <InputLabel>Tipo de Condición</InputLabel>
                        <Select name="condition_type" value={newPromo.condition_type} label="Tipo de Condición" onChange={handleInputChange}>
                            <MenuItem value="DAY_OF_WEEK">Día de la semana</MenuItem>
                            <MenuItem value="DAY_OF_MONTH">Día del mes</MenuItem>
                        </Select>
                    </FormControl></Grid>
                    <Grid item xs={12} md={6}><FormControl fullWidth>
                        <InputLabel>Valor</InputLabel>
                        <Select name="condition_value" value={newPromo.condition_value} label="Valor" onChange={handleInputChange}>
                            {newPromo.condition_type === 'DAY_OF_WEEK' ? (
                                daysOfWeek.map(day => <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>)
                            ) : (
                                [...Array(31).keys()].map(i => <MenuItem key={i + 1} value={String(i + 1)}>{i + 1}</MenuItem>)
                            )}
                        </Select>
                    </FormControl></Grid>
                    <Grid item xs={12}><FormControlLabel control={<Switch checked={newPromo.is_active} onChange={handleInputChange} name="is_active" />} label="Promoción Activa" /></Grid>
                    <Grid item xs={12}><Button variant="contained" onClick={handleCreate}>Crear Promoción</Button></Grid>
                </Grid>
            </Paper>

            <Box sx={{ height: 400, width: '100%' }}>
                <Typography variant="h6" gutterBottom>Promociones Existentes</Typography>
                <DataGrid
                    rows={promotions}
                    columns={columns}
                    localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                />
            </Box>
        </Box>
    );
};

export default PromotionManagementPage;